import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { formatNumber, formatBytes } from '@/lib/utils'
import { StreamReliabilityPanel } from '@/components/StreamReliabilityPanel'
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  Square,
  Trash2,
  Disc,
} from 'lucide-react'

interface Stats {
  total_users: number
  active_users: number
  total_streams: number
  active_streams: number
  total_sessions: number
  total_bandwidth: number
  uptime: number
}

interface ActiveSessionsPolicy {
  stale_count: number
  blocked_count: number
}

interface Viewer {
  ip: string
  username: string
  user_agent?: string
  client_type: string
  app_version?: string
  duration: string
}

interface ActiveStream {
  id: string
  name: string
  type: string
  startup_mode: string
  viewer_count: number
  viewers: Viewer[]
  is_online: boolean
  started_at?: string
  source_domain?: string
  connection_resets: number
  dts_errors: number
  speed_drops: number
  segment_discontinuities: number
  segment_duration_issues: number
  health_score: number
  health_status: string
}

interface StreamEvent {
  id: string
  stream_id: string
  stream_name: string
  event_type: string
  source_url: string
  error_message: string
  metadata?: {
    session_id?: string
    error_code?: string
    username?: string
    app?: string
    app_version?: string
    device_model?: string
    platform?: string
    network_type?: string
    duration_ms?: number
  }
  created_at: string
}

// Map client types to short display names
const clientTypeDisplay: Record<string, string> = {
  'obacast-mosaic': 'Mosaic ▦',
  'obacast-android': 'OBACast',
  'fire-tv': 'Fire TV',
  'nvidia-shield': 'Shield',
  'chromecast': 'Chromecast',
  'android-tv': 'Android TV',
  'android': 'Android',
  'apple-tv': 'Apple TV',
  'iphone': 'iPhone',
  'ipad': 'iPad',
  'vlc': 'VLC',
  'kodi': 'Kodi',
  'emby': 'Emby',
  'jellyfin': 'Jellyfin',
  'plex': 'Plex',
  'tivimate': 'TiviMate',
  'ibo-player': 'iBO',
  'smarters': 'Smarters',
  'perfect-player': 'Perfect Player',
  'ott-navigator': 'OTT Navigator',
  'xciptv': 'XCIPTV',
  'chrome': 'Chrome',
  'firefox': 'Firefox',
  'safari': 'Safari',
  'edge': 'Edge',
  'roku': 'Roku',
  'samsung-tv': 'Samsung TV',
  'lg-tv': 'LG TV',
  'unknown': 'Unknown',
}

// Health color thresholds — the control-room triad
const OK = 'hsl(150 50% 55%)'
const WARN = 'hsl(35 90% 60%)'
const ERR = 'hsl(3 100% 67%)'
const healthColor = (score: number) => (score >= 90 ? OK : score >= 70 ? WARN : ERR)

function StatCard({
  label,
  value,
  unit,
  sub,
  color,
}: {
  label: string
  value: string
  unit?: string
  sub?: string
  color?: string
}) {
  return (
    <div className="bg-card border border-border rounded-[10px] px-[18px] py-4 flex flex-col gap-2">
      <div className="cr-label">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="cr-mono text-[27px] font-semibold leading-none" style={{ color: color || 'hsl(216 29% 93%)' }}>
          {value}
        </span>
        {unit && <span className="text-xs text-cr-ink1">{unit}</span>}
      </div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  )
}

function eventSeverity(e: StreamEvent): { label: string; color: string } {
  const t = (e.event_type || '').toLowerCase()
  if (e.error_message || t.includes('error') || t.includes('fail') || t.includes('down')) {
    return { label: 'ERROR', color: ERR }
  }
  if (t.includes('buffer') || t.includes('retry') || t.includes('recovery') || t.includes('restart') || t.includes('failover') || t.includes('switch') || t.includes('degraded')) {
    return { label: 'WARN', color: WARN }
  }
  return { label: 'INFO', color: 'hsl(216 13% 42%)' }
}

export default function Dashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [controllingStream, setControllingStream] = useState<string | null>(null)
  const [expandedStream, setExpandedStream] = useState<string | null>(null)

  const { data: stats, isLoading, error } = useQuery<Stats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.getDashboardStats(),
    refetchInterval: 30000,
  })

  const { data: activeStreams } = useQuery({
    queryKey: ['active-streams'],
    queryFn: () => api.getActiveStreams(),
    refetchInterval: 10000,
  })

  const { data: activeSessionPolicy } = useQuery<ActiveSessionsPolicy>({
    queryKey: ['active-session-version-policy'],
    queryFn: () => api.getActiveSessions(),
    refetchInterval: 30000,
  })

  const streams: ActiveStream[] = activeStreams?.streams ?? []

  const { data: recentEvents } = useQuery<StreamEvent[]>({
    queryKey: ['recent-events'],
    queryFn: () => api.getRecentEvents(),
    refetchInterval: 30000,
  })

  // Recordings — both the storage tile and the active-recordings panel.
  // The server's /recordings endpoint returns everyone's recordings to
  // admin callers and only the user's own to non-admins.
  const { data: recordingStorage } = useQuery<{ used_bytes?: number; quota_bytes?: number; recording_count?: number; can_record?: boolean }>({
    queryKey: ['recording-storage'],
    queryFn: () => api.getRecordingStorage(),
    refetchInterval: 30000,
  })

  const { data: recordingsList } = useQuery<{ data?: any[]; total?: number }>({
    queryKey: ['active-recordings'],
    queryFn: () => api.getRecordings({ per_page: 200 }),
    refetchInterval: 15000,
  })

  const deleteRecordingMutation = useMutation({
    mutationFn: (id: string) => api.deleteRecording(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-recordings'] })
      queryClient.invalidateQueries({ queryKey: ['recording-storage'] })
    },
  })

  const allRecordings = recordingsList?.data ?? []
  const activeRecordings = allRecordings.filter(
    (r: any) => r.status === 'recording' || r.status === 'scheduled'
  )

  // Stream control mutations
  const stopStreamMutation = useMutation({
    mutationFn: (streamId: string) => api.stopTestStream(streamId),
    onMutate: (streamId) => setControllingStream(streamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-streams'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
    onSettled: () => setControllingStream(null),
  })

  const restartStreamMutation = useMutation({
    mutationFn: (streamId: string) => api.testStream(streamId),
    onMutate: (streamId) => setControllingStream(streamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-streams'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
    onSettled: () => setControllingStream(null),
  })

  const handleStopStream = (e: React.MouseEvent, streamId: string) => {
    e.stopPropagation()
    if (confirm('Stop this stream? Viewers will be disconnected.')) {
      stopStreamMutation.mutate(streamId)
    }
  }

  const handleRestartStream = (e: React.MouseEvent, streamId: string) => {
    e.stopPropagation()
    restartStreamMutation.mutate(streamId)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        <AlertCircle className="w-5 h-5 mr-2" />
        Failed to load dashboard data
      </div>
    )
  }

  // Derived: health rollup across running streams
  const healthyCount = streams.filter((s) => (s.health_score ?? 100) >= 90).length
  const degradedCount = streams.filter((s) => {
    const h = s.health_score ?? 100
    return h < 90 && h >= 70
  }).length
  const criticalCount = streams.filter((s) => (s.health_score ?? 100) < 70).length
  const avgHealth = streams.length
    ? streams.reduce((a, s) => a + (s.health_score ?? 100), 0) / streams.length
    : 100

  // Top streams by viewers
  const topStreams = [...streams].sort((a, b) => b.viewer_count - a.viewer_count).slice(0, 8)
  const maxViewers = Math.max(...topStreams.map((s) => s.viewer_count), 1)

  // Sessions table: one row per connected viewer across all running streams
  const sessionRows = streams.flatMap((s) =>
    (s.viewers ?? []).map((v, i) => ({
      key: `${s.id}-${i}`,
      user: v.username,
      ip: v.ip.split(':')[0],
      client: clientTypeDisplay[v.client_type] || v.client_type || 'Unknown',
      appVersion: v.app_version,
      userAgent: v.user_agent,
      stream: s.name,
      streamId: s.id,
      source: s.source_domain || '—',
      health: Math.round(s.health_score ?? 100),
      duration: v.duration,
    }))
  )

  // Recent server and app playback issues. Routine INFO noise stays elsewhere.
  const errorEvents = (recentEvents ?? [])
    .map((e) => ({ ...e, sev: eventSeverity(e) }))
    .filter((e) => e.sev.label !== 'INFO')
    .slice(0, 8)

  return (
    <div className="flex flex-col gap-4">
      {/* Stat cards */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        <StatCard
          label="STREAM HEALTH"
          value={streams.length ? avgHealth.toFixed(1) : '—'}
          unit={streams.length ? '%' : undefined}
          sub={
            streams.length
              ? `${healthyCount} healthy · ${degradedCount} degraded · ${criticalCount} critical`
              : 'no streams running'
          }
          color={streams.length ? healthColor(avgHealth) : undefined}
        />
        <StatCard
          label="ACTIVE VIEWERS"
          value={formatNumber(stats?.total_sessions || 0)}
          unit="sessions"
          sub={`${stats?.active_users || 0} of ${stats?.total_users || 0} users active`}
        />
        <StatCard
          label="CLIENT HEALTH"
          value={String(activeSessionPolicy?.blocked_count ?? 0)}
          unit="blocked"
          sub={`${activeSessionPolicy?.stale_count ?? 0} below recommended build`}
          color={(activeSessionPolicy?.blocked_count ?? 0) > 0 ? ERR : (activeSessionPolicy?.stale_count ?? 0) > 0 ? WARN : OK}
        />
        <StatCard
          label="STREAMS UP"
          value={String(stats?.active_streams ?? 0)}
          unit={`/ ${stats?.total_streams ?? 0}`}
          sub="running now"
        />
        <StatCard
          label="BANDWIDTH"
          value={formatBytes(stats?.total_bandwidth || 0)}
          sub="current usage"
        />
        <StatCard
          label="RECORDINGS"
          value={String(activeRecordings.length)}
          unit={recordingsList?.total !== undefined ? `/ ${recordingsList.total} total` : undefined}
          sub={
            recordingStorage?.quota_bytes
              ? `${formatBytes(recordingStorage.used_bytes || 0)} used · ${formatBytes(recordingStorage.quota_bytes - (recordingStorage.used_bytes || 0))} free`
              : `${formatBytes(recordingStorage?.used_bytes || 0)} used`
          }
          color={activeRecordings.some((r: any) => r.status === 'recording') ? ERR : undefined}
        />
      </div>

      {/* Top streams + Recent errors */}
      <div className="grid gap-3 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {/* Top streams now */}
        <div className="bg-card border border-border rounded-[10px]">
          <div className="flex items-center justify-between px-[18px] py-3.5 border-b border-cr-soft">
            <div className="font-semibold text-[13px]">Top streams now</div>
            <span className="cr-label">BY VIEWERS</span>
          </div>
          <div className="px-[18px] pt-2.5 pb-4 flex flex-col gap-2.5">
            {topStreams.length === 0 && (
              <div className="text-sm text-muted-foreground py-2">No streams running.</div>
            )}
            {topStreams.map((s) => (
              <div key={s.id} className="flex flex-col gap-[5px]">
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={() => setExpandedStream(expandedStream === s.id ? null : s.id)}
                    className="flex items-center gap-2 min-w-0 text-left hover:text-primary transition-colors"
                    title="Show source reliability"
                  >
                    <span
                      className="cr-dot"
                      style={{
                        width: 6,
                        height: 6,
                        background: healthColor(s.health_score ?? 100),
                      }}
                    />
                    <span className="text-[13px] truncate">{s.name}</span>
                    {s.viewers?.some((v) => v.client_type === 'obacast-mosaic') && (
                      <span className="cr-mono text-[9px] shrink-0" style={{ color: 'hsl(300 70% 70%)' }}>▦ MOSAIC</span>
                    )}
                    {s.source_domain && (
                      <span className="cr-mono text-[10px] text-muted-foreground whitespace-nowrap hidden sm:inline">
                        {s.source_domain}
                      </span>
                    )}
                  </button>
                  <span className="flex items-center gap-2.5 shrink-0">
                    <span className="cr-mono text-[11px]" style={{ color: healthColor(s.health_score ?? 100) }}>
                      {Math.round(s.health_score ?? 100)}
                    </span>
                    <span className="cr-mono text-xs text-cr-ink1">{s.viewer_count}</span>
                    <button
                      onClick={(e) => handleRestartStream(e, s.id)}
                      disabled={controllingStream === s.id}
                      className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                      title="Restart stream"
                    >
                      {controllingStream === s.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      onClick={(e) => handleStopStream(e, s.id)}
                      disabled={controllingStream === s.id}
                      className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                      title="Stop stream"
                    >
                      <Square className="w-3.5 h-3.5" />
                    </button>
                  </span>
                </div>
                <div className="h-[3px] bg-muted rounded-sm overflow-hidden">
                  <div
                    className="h-full rounded-sm"
                    style={{
                      width: `${Math.round((s.viewer_count / maxViewers) * 100)}%`,
                      background: 'hsl(var(--primary))',
                    }}
                  />
                </div>
                {expandedStream === s.id && (
                  <div className="bg-popover border border-cr-soft rounded-lg px-3 mt-1">
                    <StreamReliabilityPanel streamId={s.id} />
                    <button
                      onClick={() => navigate(`/streams?edit=${s.id}`)}
                      className="cr-mono text-[10px] tracking-[0.1em] text-primary hover:brightness-110 pb-2"
                    >
                      EDIT STREAM →
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recent playback issues */}
        <div className="bg-card border border-border rounded-[10px]">
          <div className="flex items-center justify-between px-[18px] py-3.5 border-b border-cr-soft">
            <div className="font-semibold text-[13px]">Recent playback issues</div>
            <span className="cr-label">SERVER + APP</span>
          </div>
          <div className="px-[18px] py-1.5">
            {errorEvents.length === 0 && (
              <div className="text-sm text-muted-foreground py-3">No recent playback issues. 🎉</div>
            )}
            {errorEvents.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => e.metadata?.session_id && navigate(`/analytics?session=${encodeURIComponent(e.metadata.session_id)}`)}
                disabled={!e.metadata?.session_id}
                className="grid w-full items-center gap-3 py-2 text-left cr-row last:border-b-0 enabled:cursor-pointer enabled:hover:bg-muted/35 disabled:cursor-default"
                style={{ gridTemplateColumns: '58px 52px 1fr auto' }}
                title={e.metadata?.session_id ? 'Open the complete playback session and error timeline' : (e.error_message || e.event_type)}
              >
                <span className="cr-mono text-[11px] text-muted-foreground">
                  {new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="cr-mono text-[10px]" style={{ color: e.sev.color }}>
                  {e.sev.label}
                </span>
                <span className="text-[13px] text-cr-ink1 truncate" title={e.error_message || e.event_type}>
                  {e.error_message || e.event_type}
                  <span className="cr-mono text-[10px] text-muted-foreground ml-2">{e.stream_name}</span>
                </span>
                {e.metadata?.session_id && (
                  <span className="cr-mono text-[10px] text-primary whitespace-nowrap">DETAILS →</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Active recordings (only when there are any) */}
      {activeRecordings.length > 0 && (
        <div className="bg-card border border-border rounded-[10px]">
          <div className="flex items-center justify-between px-[18px] py-3.5 border-b border-cr-soft">
            <div className="flex items-center gap-2 font-semibold text-[13px]">
              <Disc className="w-4 h-4 text-destructive" />
              Active recordings
              <span className="cr-mono text-[11px] text-muted-foreground font-normal">{activeRecordings.length}</span>
            </div>
          </div>
          <div>
            {activeRecordings.map((rec: any) => {
              const isLive = rec.status === 'recording'
              const startMs = new Date(rec.start_time).getTime()
              const endMs = new Date(rec.end_time).getTime()
              const total = endMs - startMs
              const elapsed = Date.now() - startMs
              const progress = total > 0 ? Math.max(0, Math.min(100, (elapsed / total) * 100)) : 0
              const remainingMin = Math.max(0, Math.round((endMs - Date.now()) / 60000))
              return (
                <div key={rec.id} className="px-[18px] py-3 flex items-center gap-4 cr-row last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isLive ? (
                        <span className="inline-flex items-center gap-1 cr-mono text-[10px] text-destructive">
                          <span className="cr-dot cr-dot-err cr-pulse" style={{ width: 5, height: 5 }} />
                          REC
                        </span>
                      ) : (
                        <span className="cr-mono text-[10px] text-cr-info">SCHEDULED</span>
                      )}
                      <span className="text-[13px] font-medium truncate">{rec.title}</span>
                    </div>
                    <div className="cr-mono text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span>{rec.channel_name || rec.channel_id?.slice(0, 8)}</span>
                      {rec.requested_by && <span>· by {rec.requested_by}</span>}
                      <span>
                        · {isLive ? `${remainingMin} min left` : `starts ${new Date(rec.start_time).toLocaleTimeString()}`}
                      </span>
                    </div>
                    {isLive && (
                      <div className="mt-2 h-[3px] bg-muted rounded-sm overflow-hidden">
                        <div className="h-full bg-destructive transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (confirm(`${isLive ? 'Stop' : 'Cancel'} recording "${rec.title}"?`)) {
                        deleteRecordingMutation.mutate(rec.id)
                      }
                    }}
                    className="p-2 rounded text-muted-foreground hover:text-destructive transition-colors"
                    title={isLive ? 'Stop recording' : 'Cancel scheduled recording'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Active sessions */}
      <div className="bg-card border border-border rounded-[10px] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-3.5 border-b border-cr-soft">
          <div className="flex items-center gap-2 font-semibold text-[13px]">
            {sessionRows.length > 0 && <span className="cr-dot cr-dot-ok cr-pulse" style={{ width: 6, height: 6 }} />}
            Active sessions
            <span className="cr-mono text-[11px] text-muted-foreground font-normal">{sessionRows.length}</span>
          </div>
          <button
            onClick={() => navigate('/streams')}
            className="cr-mono text-[10px] tracking-[0.1em] text-primary hover:brightness-110"
          >
            STREAMS →
          </button>
        </div>
        {sessionRows.length === 0 ? (
          <div className="px-[18px] py-6 text-sm text-muted-foreground">No one is watching right now.</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[850px]">
              <div
                className="grid gap-3 px-[18px] py-[9px] border-b border-border cr-label"
                style={{ gridTemplateColumns: '110px 115px 120px 90px 1fr 120px 60px 70px' }}
              >
                <span>USER</span>
                <span>IP</span>
                <span>CLIENT</span>
                <span>APP VERSION</span>
                <span>WATCHING</span>
                <span>SOURCE</span>
                <span>HEALTH</span>
                <span>TIME</span>
              </div>
              {sessionRows.map((row) => (
                <div
                  key={row.key}
                  className="grid gap-3 items-center px-[18px] py-2 cr-row last:border-b-0 text-[13px] hover:bg-muted/60 transition-colors"
                  style={{ gridTemplateColumns: '110px 115px 120px 90px 1fr 120px 60px 70px' }}
                >
                  <span className="font-medium truncate">{row.user}</span>
                  <span className="cr-mono text-[11px] text-cr-ink1">{row.ip}</span>
                  <span className="cr-mono text-[11px] text-cr-ink1 truncate" title={row.userAgent}>
                    {row.client}
                  </span>
                  <span className="cr-mono text-[11px] text-cr-ink1">
                    {row.appVersion ? `v${row.appVersion}` : '—'}
                  </span>
                  <button
                    onClick={() => navigate(`/streams?edit=${row.streamId}`)}
                    className="text-xs text-left truncate hover:text-primary transition-colors"
                  >
                    {row.stream}
                  </button>
                  <span className="cr-mono text-[10px] text-primary truncate">{row.source}</span>
                  <span className="cr-mono text-xs" style={{ color: healthColor(row.health) }}>
                    {row.health}
                  </span>
                  <span className="cr-mono text-[11px] text-muted-foreground">{row.duration}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
